import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import styled from 'styled-components/macro';
import { theme } from 'lib/style';
import { NewModal } from 'lib/components';
import { Tag } from 'react-covideo-common';
import { useAuth } from 'lib/context';
import { useGetDrop } from 'lib/api/droplr/getDrop';
import { useGetBoards } from 'lib/api/droplr/getBoards';
import { CombinedBoard } from 'lib/api/droplr/types';
import { useEditDrops } from 'lib/api/droplr/editDrops';
import { droplrModalsSelectStyle } from 'lib/style/commonStyles';
import { useIsMutating } from 'react-query';
import { droplrKeys } from 'lib/api/droplr/queryKeys';
import { Button } from 'react-covideo-common';
import { TagType } from 'react-covideo-common/dist/components/Tag/types';
import { useTranslation } from 'react-i18next';

const SelectInput = styled(Select)`
  position: relative;
  display: block;
  width: 528px;
  font-size: 16px;
  height: 40px;
  box-sizing: border-box;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  border: none;
  box-sizing: border-box;
  &:focus {
    color: #495057;
    background-color: ${theme.palette.white};
    outline: 0;
  }
`;

const BoardsContainer = styled.div`
  height: 250px;
  display: flex;
  flex-direction: row;
  margin-top: 18px;
  flex-flow: row wrap;
  align-content: flex-start;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

type Props = {
  dropIds: string[];
  handleModalClose: () => void;
};

type Option = {
  label: string;
  value: string;
};

export const SetBoardsModal = ({ handleModalClose, dropIds }: Props) => {
  const { t } = useTranslation();
  const [inputValue, onInputChange] = useState('');
  const [selectedValues, setSelectedValues] = useState([] as Option[]);

  const { userData } = useAuth();

  const isSingularDrop = dropIds.length === 1;

  const { data: drop, refetch: refetchDrop } = useGetDrop({
    dropId: isSingularDrop ? dropIds[0] : '',
  });

  const { data: boardsResult, refetch: refetchBoards } = useGetBoards({
    userId: Number(userData.id),
    customerId: Number(userData.customerId),
  });

  // board options
  const boards = (boardsResult?.boards || [])
    .filter((board: CombinedBoard) => board.userStatus !== 'readCollaborator')
    .map((board: CombinedBoard) => ({
      label: board.name,
      value: board.id,
    }));

  const { mutateAsync: editDrops } = useEditDrops(t);
  const isLoading =
    useIsMutating({
      mutationKey: droplrKeys.edit_drop_mutation(),
    }) > 0;

  useEffect(() => {
    if (isSingularDrop && drop?.boards && drop.boards.length) {
      // get board names for chosen drop
      const dropBoards = (boardsResult?.boards || []).filter(
        (board: CombinedBoard) => (drop.boards || []).includes(board.id)
      );
      setSelectedValues(
        dropBoards.map(dropBoard => ({
          label: dropBoard.name,
          value: dropBoard.id,
        }))
      );
    }
  }, [drop]);

  const removeBoard = (board: TagType) => {
    setSelectedValues(
      selectedValues.filter((b: Option) => b.value !== board.value)
    );
  };

  const onChange = (newValue: unknown) => {
    setSelectedValues(newValue as Option[]);
  };

  const updateBoards = async () => {
    const boards = selectedValues.map(b => b.value);
    await editDrops(
      dropIds.map(id => ({
        dropId: id,
        boards,
      }))
    );

    refetchDrop();
    refetchBoards();
  };

  return (
    <NewModal
      closeModal={handleModalClose}
      headerText={t('Manage Boards')}
      style={{
        content: {
          width: '528px',
          minWidth: '528px',
          height: '400px',
        },
      }}
    >
      <div>
        <SelectInput
          inputValue={inputValue}
          onInputChange={onInputChange}
          styles={droplrModalsSelectStyle}
          placeholder={t('Add boards(s)...')}
          value={selectedValues}
          onChange={newValue => onChange(newValue)}
          options={boards}
          noOptionsMessage={() => null}
          isMulti={true}
          isClearable={false}
        />

        <BoardsContainer>
          {selectedValues &&
            !!selectedValues.length &&
            selectedValues.map((board: Option) => (
              <Tag key={board.value} tag={board} onRemove={removeBoard} />
            ))}
        </BoardsContainer>

        <Footer>
          <Button
            onClick={async () => {
              await updateBoards();
              handleModalClose();
            }}
            text={t('Save')}
            type='submit'
            disabled={isLoading}
          />
        </Footer>
      </div>
    </NewModal>
  );
};
